// @flow

import type {DispositionNode, EditableGraph, Id} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useReleaseFlag} from 'src/hooks/product-flags';
import {findAncestorNodeVariables} from 'src/components/conversation-builder/utils';
import {createRawDraftContentState, hasText} from 'src/utils/draft';

import {useEditorState} from './reducer';
import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';
import {validateNode} from './validation';
import {useOWBFields} from '../hooks';

import type {PossibleAnswer} from 'src/types/writeback';

import EditorContainer from './container.jsx';
import {FormPair} from 'src/components/conversation-builder/form.jsx';
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor.jsx';
import {PadSection} from './common.jsx';
import LoadedVariablePicker from './variable-picker.jsx';
import NumberInput from 'src/components/lib/number-input';
import PhoneInput from 'src/components/lib/phone-input';
import {Select} from 'src/components/lib/token-list-input';


export default function DispositionEditor({
  defaultNode,
  entityType,
  hasSubmitted,
  graph,
  edgeId,
  ...props
}: {
  graph: EditableGraph,
  entityType: EntityType,
  onClose: () => mixed,
  onSubmit: (DispositionNode) => mixed,
  defaultNode: DispositionNode,
  actionName: 'add' | 'edit',
  hasSubmitted: boolean,
  readOnly: boolean,
  edgeId?: Id,
}): React.Node {
  const {readOnly} = props;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const [state, dispatch] = useEditorState({
    defaultNode,
  });
  const {node} = state;
  const update = (payload: $Shape<DispositionNode>) =>
    dispatch({type: 'update', payload});
  const showFileUploadNodeVar = Boolean(
    useReleaseFlag('chatbotFileUploadLinkNotificationEnabled'),
  );

  const nodeVariables = findAncestorNodeVariables(
    graph,
    defaultNode,
    edgeId,
    showFileUploadNodeVar,
  );
  // TODO (kyle): this causes some stuff to pop in because the writeback
  // info has to load async.
  const writebackAttributes = useWritebackMetadataV2(entityType);

  const {getWbFieldId} = useOWBFields();

  const {writeback_field_id} = node;
  const attributeWritebackInfo =
    writeback_field_id && writebackAttributes
      ? [...writebackAttributes.values()].find(
          (metadata) => getWbFieldId(metadata) === writeback_field_id,
        )
      : null;

  const dataType = attributeWritebackInfo?.data_type || 'string';

  const error = hasSubmitted ? validateNode(node, graph) : null;

  let valueInput;
  const possibleValues =
    attributeWritebackInfo?.possible_answers ||
    attributeWritebackInfo?.possible_values;
  if (possibleValues) {
    valueInput = (
      <StringSelect
        searchable
        options={possibleValues}
        value={String(node.disposition_writeback_value)}
        onChange={(disposition_writeback_value) =>
          update({disposition_writeback_value})
        }
        disabled={readOnly}
      />
    );
  } else {
    switch (dataType) {
      case 'date':
      case 'email': //TODO: date and email should probably be validated too
      case 'string':
        valueInput = (
          <MessageDraftEditor
            label=""
            placeholder={`${t(
              'Enter_a_value_for_the_ATS_field',
              'Enter a value for the ATS field',
            )}...`}
            defaultValue={node.text_draft_json || createRawDraftContentState()}
            onChange={(text_draft_json) => update({text_draft_json})}
            entityType={entityType}
            isFirstSmsMessage={false}
            allowGifs={false}
            allowEmojis={false}
            error={error?.text_draft_json}
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        );
        break;

      case 'currency':
      case 'number':
      case 'integer':
      case 'double':
        valueInput = (
          <NumberInput
            isFloat={dataType === 'currency' || dataType === 'double'}
            placeholder={`${t(
              'Enter_a_value_for_the_ATS_field',
              'Enter a value for the ATS field',
            )}...`}
            value={Number(node.disposition_writeback_value)}
            onChange={(disposition_writeback_value) => {
              update({
                disposition_writeback_value,
              });
            }}
            disabled={readOnly}
          />
        );
        break;

      case 'phone':
        valueInput = (
          <PhoneInput
            placeholder={`${t(
              'Enter_a_value_for_the_ATS_field',
              'Enter a value for the ATS field',
            )}...`}
            value={String(node.disposition_writeback_value || '')}
            onChange={(disposition_writeback_value) => {
              update({
                disposition_writeback_value,
              });
            }}
            disabled={readOnly}
          />
        );
        break;

      default:
        throw new Error('Invalid writeback data type.');
    }
  }

  return (
    <EditorContainer
      onUpdate={update}
      state={state}
      attributeWritebackInfo={attributeWritebackInfo}
      disabled={
        !node.writeback_field_id ||
        (!node.disposition_writeback_value &&
          node.text_draft_json &&
          !hasText(node.text_draft_json))
      }
      error={error}
      {...props}
    >
      <PadSection>
        <FormPair label={t('ATS_Field', 'ATS Field')}>
          <LoadedVariablePicker
            entityType={entityType}
            value={node.writeback_field_id}
            attributeMetadata={writebackAttributes}
            onChange={(attributeMetadata) => {
              const writebackFieldId = getWbFieldId(attributeMetadata);
              dispatch({
                type: 'set_writeback',
                payload: writebackFieldId,
                metadata: {
                  previous: attributeWritebackInfo,
                  next: attributeMetadata,
                },
              });
              if (
                attributeMetadata?.possible_values ||
                attributeMetadata?.data_type !== 'string'
              ) {
                update({
                  text_draft_json: null,
                });
              } else {
                update({
                  disposition_writeback_value: null,
                });
              }
            }}
            readOnly={readOnly}
          />
        </FormPair>
        <FormPair label={t('Value', 'Value')} TagName="div">
          {valueInput}
        </FormPair>
      </PadSection>
    </EditorContainer>
  );
}

// NOTE (kyle): might want to make this a common component?
function StringSelect({
  value,
  options,
  onChange,
  ...props
}: {
  ...React.ElementConfig<typeof Select>,
  value: ?string,
  options: string[] | PossibleAnswer[],
  onChange: (?string) => mixed,
}) {
  const optionObjects = React.useMemo(() =>
    options.map((possibleVal) => ({
      value: typeof possibleVal === 'string' ? possibleVal : possibleVal.value,
      label: typeof possibleVal === 'string' ? possibleVal : possibleVal.label,
    })),
  );

  const selectedOption = optionObjects.find((option) => option.value === value);

  return (
    <Select
      {...props}
      value={selectedOption}
      options={optionObjects}
      onChange={(option) => onChange(option?.value)}
    />
  );
}
